import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { z } from "zod";

import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { StakeholderDrawerContent } from "@/presentation/components/stakeholders/stakeholder-drawer-content";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";

const stakeholdersSearchSchema = z.object({
  id: z.string().optional(),
});

export const Route = createFileRoute("/_app/stakeholders/")({
  validateSearch: stakeholdersSearchSchema,
  component: StakeholdersListPage,
});

function StakeholdersListPage() {
  const listQ = useStakeholdersList();
  const navigate = useNavigate({ from: Route.fullPath });
  const { id: openId } = Route.useSearch();
  const items = listQ.data ?? [];

  const grouped = new Map<string, typeof items>();
  for (const s of items) {
    const arr = grouped.get(s.org) ?? [];
    arr.push(s);
    grouped.set(s.org, arr);
  }

  const orgEntries = [...grouped.entries()];
  const topOrgs = orgEntries.slice(0, 4).map(([org, people]) => ({
    label: org,
    value: people.length,
  }));

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Roster"
        icon={Users}
        numeral={String(items.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix={`across ${orgEntries.length} org${orgEntries.length === 1 ? "" : "s"}`}
        title="Who's shaping the cycle"
        summary="Decision-makers, technical leads, and vertical owners we're working with this assessment."
        meta={
          topOrgs.length > 0 ? topOrgs : [{ label: "On file", value: items.length, tone: "cyan" }]
        }
      />

      <div className="stagger-children flex flex-col gap-6">
        {[...grouped.entries()].map(([org, people]) => (
          <section key={org} className="glow-card flex flex-col gap-3 p-5">
            <h2 className="eyebrow">{org}</h2>
            <ul className="flex flex-col">
              {people.map((s) => (
                <li
                  key={s.id}
                  className="border-border/40 flex items-center justify-between border-t py-2 first:border-t-0 print:break-inside-avoid"
                >
                  <button
                    type="button"
                    onClick={() => navigate({ search: { id: s.id } })}
                    className="hover:bg-muted/30 -mx-2 flex flex-1 items-center justify-between rounded-sm px-2 py-1 text-left transition"
                    aria-label={`Edit ${s.name}`}
                  >
                    <div>
                      <p className="text-xs">{s.name}</p>
                      {s.role ? (
                        <p className="text-muted-foreground text-[10px]">{s.role}</p>
                      ) : null}
                    </div>
                    <span className="text-muted-foreground font-data text-[10px] tracking-[0.14em] uppercase">
                      {s.area ?? ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <Sheet
        open={!!openId}
        onOpenChange={(open) => {
          if (!open) navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Stakeholder detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the stakeholder.</SheetDescription>
          {openId ? <StakeholderDrawerContent id={openId} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
